/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  CommonAddButton,
  CommonButton,
} from '../../../../../components/button/main';
import { CommonSelect } from '../../../../../components/select';
import getFormatMessage from '../../../../../locales/get-format-message';
import { ILastQuery, ScopeDrawer } from './drawer';
import styles from './index.module.scss';
import {
  IApiGCloudGameItem,
  INoticeSystemScopeResult,
} from '../../../../../model/api-modules';
import { isEqual, uniq, uniqWith } from 'lodash';
import { AutoTable } from '../../../../../components/auto-table';
import { downloadXlsx } from './lib';
import { Divider } from 'antd';
import { PaginationTable } from '../../../../../components/pagination-table';
import { ModalWrap } from '../../../../../components/modal-wrap';

export interface IScopeValue {
  condition: { type: string; condition: Object };
  result: readonly INoticeSystemScopeResult[];
  notSelected: readonly INoticeSystemScopeResult[];
}

interface IProps {
  readonly onChange?: (scopes?: IScopeValue) => void;
  readonly value?: IScopeValue;
  readonly readOnly?: boolean;
}

export const ScopeSelector = (props: IProps) => {
  const { value, onChange, readOnly } = props;
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [scopeResult, setScopeResult] = useState<IScopeValue>();

  useEffect(() => {
    if (!value) {
      return;
    }
    setScopeResult(value);
  }, [value]);

  const getConditionResult = (
    list: IApiGCloudGameItem[],
    allList: IApiGCloudGameItem[]
  ) => {
    const getFormattedList = (list: IApiGCloudGameItem[]) =>
      list.reduce<Record<number, INoticeSystemScopeResult>>((acc, listItem) => {
        const { gameId } = listItem;
        const currentSdkList = [...listItem.sdkList].map((item) => {
          return { ...item, gcloudVersion: listItem.gcloudVersion };
        });
        if (!acc[gameId]) {
          acc[gameId] = {
            gameName: listItem.gameName,
            gameID: String(listItem.gameId),
            members: listItem.memberList
              .map((item) => item.email)
              .filter(Boolean),
            adminMembers: listItem.memberList
              .filter((item) => item.roleId === 0)
              .map((item) => item.email)
              .filter(Boolean),
            sdkList: currentSdkList,
            downloadUser: listItem.downloadUser,
            isBindKpiCode: listItem.isBindKpiCode,
          };
          return acc;
        }

        acc[gameId] = {
          ...acc[gameId],
          sdkList: uniqWith(
            [...acc[gameId].sdkList, ...currentSdkList],
            isEqual
          ),
        };

        return acc;
      }, {});

    const selectedResult = Object.values(getFormattedList(list));
    const unSelectedResult = Object.values(
      getFormattedList(
        allList.filter((listItem) => {
          return list.every((subListItem) => subListItem.id !== listItem.id);
        })
      )
    );
    return {
      selectedResult,
      unSelectedResult,
    };
  };

  const onScopeChange = (changes: ILastQuery) => {
    if (!changes.filter || !changes.result || !changes.allList) {
      return;
    }

    const conditionResult = getConditionResult(changes.result, changes.allList);
    const scopeValue: IScopeValue = {
      condition: {
        type: changes.filter.type,
        condition: changes.filter.searchValues,
      },
      result: conditionResult.selectedResult,
      notSelected: conditionResult.unSelectedResult,
    };
    setScopeResult(scopeValue);
    onChange?.(scopeValue);
    setShowDrawer(false);
  };

  const exportList = () => {
    downloadXlsx(scopeResult);
  };

  const renderContent = () => {
    const getSummary = (value: IScopeValue) =>
      getFormatMessage('notice_selected_summary', {
        params: {
          gameCount: value.result.length,
          memberCount: uniq(value.result.map((item) => item.members).flat())
            .length,
        },
      });

    const reAdd = () => {
      ModalWrap.show({
        title: getFormatMessage('notice_confirm_reset'),
        children: getFormatMessage('notice_reset_warning'),
        oKText: getFormatMessage('Confirm'),
        cancelText: getFormatMessage('cancel'),
        async onOk() {
          setShowDrawer(true);
          setScopeResult(undefined);
          onChange?.(undefined);
          return true;
        },
      });
    };

    const renderButton = () => {
      if (readOnly) {
        return null;
      }
      if (scopeResult) {
        return (
          <CommonAddButton onClick={reAdd} className={styles.reAddBtn}>
            {getFormatMessage('notice_reset')}
          </CommonAddButton>
        );
      }
      return (
        <CommonAddButton onClick={() => setShowDrawer(true)}>
          {getFormatMessage('notice_add')}
        </CommonAddButton>
      );
    };

    if (readOnly && value) {
      return (
        <div className={styles.readOnlyScope}>
          <span>{getFormatMessage('notice_gcloud_biz')}</span>
          <Divider type="vertical" />
          <span>
            <span> {getFormatMessage('notice_selected')}</span>
            {getSummary(value)}
            <CommonButton type="link" onClick={exportList}>
              | {getFormatMessage('notice_export')} {'>'}
            </CommonButton>
          </span>
        </div>
      );
    }

    return (
      <>
        <CommonSelect
          disabled
          value={getFormatMessage('notice_gcloud_biz')}
          after={renderButton()}
        />
        {!!scopeResult && (
          <span>
            <span> {getFormatMessage('notice_selected')}</span>
            <CommonButton type="link" onClick={exportList}>
              {getSummary(scopeResult)}
              {`｜${getFormatMessage('notice_export')} >`}
            </CommonButton>
          </span>
        )}
      </>
    );
  };

  return (
    <div className={styles.scopeSelect}>
      <div className={styles.scopeCondition}>{renderContent()}</div>
      <div>
        {!!scopeResult && !readOnly && (
          <PaginationTable
            list={scopeResult.result}
            configFields={[
              {
                key: 'gameID',
                name: getFormatMessage('gameID'),
              },
              {
                key: 'gameName',
                name: getFormatMessage('game_name'),
              },
              {
                key: 'downloadUser',
                name: getFormatMessage('notice_creator'),
              },
              {
                key: 'memberCount',
                name: getFormatMessage('notice_member_count'),
                tableExtension: {
                  renderRow(key, val, record) {
                    return record.members.length;
                  },
                },
              },
            ]}
          />
        )}
      </div>
      {showDrawer && (
        <ScopeDrawer
          onClose={() => setShowDrawer(false)}
          onChange={onScopeChange}
        />
      )}
    </div>
  );
};
